<template>
  <div>
    <div class="map" ref="map"></div>
  </div>
</template>
<script>
import "mapbox-gl/dist/mapbox-gl.css";
import states from "@/data/states";
import bbox from "@turf/bbox";
import { isHeadless } from "@/helpers";
import mapboxgl from "mapbox-gl";

export default {
  data() {
    return {
      map: null,
      marker: null,
      popup: null,
      fitBounds: false,
      currentLayer: null
    };
  },
  compatConfig: {
    WATCH_ARRAY: false
  },
  props: {
    title: {
      type: String
    },
    center: {
      type: Array,
      default() {
        return [-100, 38];
      }
    },
    bbox: {
      type: Array
    },
    disableSelection: {
      type: Boolean,
      default: false
    },
    selectedState: {
      type: String
    },
    selectedCounty: {
      type: String
    },
    selectedFeature: {
      type: Object
    },
    feature: {
      type: String
    },
    showMarker: {
      type: Boolean,
      default: false
    },
    showNameOnHover: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    style() {
      let colorScheme = this.$store.state.colorScheme;
      if (colorScheme === "auto") {
        if (
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
        ) {
          colorScheme = "dark";
        } else {
          colorScheme = "light";
        }
      }
      return colorScheme === "dark"
        ? "mapbox://styles/mapbox/dark-v10"
        : "mapbox://styles/mapbox/light-v10";
    },
    featureDetails() {
      const features = {
        schoolDistrict: {
          tiles: [
            "https://gis.data.census.gov/arcgis/rest/services/Hosted/VT_2019_970_00_PY_D1/VectorTileServer/tile/{z}/{y}/{x}.pbf"
          ],
          layer: "UnifiedSchoolDistrict",
          labelLayer: "UnifiedSchoolDistrict/label",
          labelField: "_name",
          zoom: 8
        },
        elementarySchoolDistrict: {
          tiles: [
            "https://gis.data.census.gov/arcgis/rest/services/Hosted/VT_2019_950_00_PY_D1/VectorTileServer/tile/{z}/{y}/{x}.pbf"
          ],
          layer: "ElementarySchoolDistrict",
          labelLayer: "ElementarySchoolDistrict/label",
          labelField: "_name",
          zoom: 8
        },
        secondarySchoolDistrict: {
          tiles: [
            "https://gis.data.census.gov/arcgis/rest/services/Hosted/VT_2019_960_00_PY_D1/VectorTileServer/tile/{z}/{y}/{x}.pbf"
          ],
          layer: "SecondarySchoolDistrict",
          labelLayer: "SecondarySchoolDistrict/label",
          labelField: "_name",
          zoom: 8
        },
        township: {
          tiles: [
            "https://gis.data.census.gov/arcgis/rest/services/Hosted/VT_2019_060_00_PY_D1/VectorTileServer/tile/{z}/{y}/{x}.pbf"
          ],
          layer: "CountySubdivision",
          labelLayer: "CountySubdivision/label",
          labelField: "_name",
          zoom: 8
        },
        county: {
          tiles: [
            "https://gis.data.census.gov/arcgis/rest/services/Hosted/VT_2019_050_00_PY_D1/VectorTileServer/tile/{z}/{y}/{x}.pbf"
          ],
          layer: "County",
          labelLayer: "County/label",
          labelField: "BASENAME",
          zoom: 8
        },
        zipcode: {
          tiles: [
            "https://gis.data.census.gov/arcgis/rest/services/Hosted/VT_2019_860_00_PY_D1/VectorTileServer/tile/{z}/{y}/{x}.pbf"
          ],
          layer: "ZCTA5",
          labelLayer: "ZCTA5/label",
          labelField: "BASENAME",
          zoom: 10.5
        },
        state: {
          tiles: [
            "https://gis.data.census.gov/arcgis/rest/services/Hosted/VT_2019_040_00_PP_D1/VectorTileServer/tile/{z}/{y}/{x}.pbf"
          ],
          layer: "State",
          labelLayer: "STATENAME",
          labelField: "_name",
          zoom: 8
        }
      };
      if (!this.feature || !(this.feature in features)) {
        return null;
      }
      return features[this.feature];
    },
    zoom() {
      return this.featureDetails ? this.featureDetails.zoom : 16;
    }
  },
  methods: {
    renderFeatures() {
      if (!this.map.loaded()) {
        return;
      }
      if (
        this.featureDetails &&
        this.currentLayer === this.featureDetails.layer
      ) {
        return;
      }
      if (this.map.getSource("features")) {
        this.map.removeLayer("features-line");
        this.map.removeLayer("features-fill");
        this.map.removeLayer("features-fill-selected");
        this.map.removeLayer("features-labels");
        this.map.removeSource("features");
      }
      if (!this.featureDetails) {
        return;
      }

      this.currentLayer = this.featureDetails.layer;
      this.map.addSource("features", {
        type: "vector",
        tiles: this.featureDetails.tiles,
        promoteId: "GEOID"
      });
      this.map.addLayer({
        id: "features-line",
        type: "line",
        source: "features",
        "source-layer": this.featureDetails.layer,
        paint: {
          "line-opacity": 0.7,
          "line-width": 2,
          "line-color": "#1e86e9"
        }
      });
      this.map.addLayer({
        id: "features-fill",
        type: "fill",
        source: "features",
        "source-layer": this.featureDetails.layer,
        paint: {
          "fill-opacity": 0.1,
          "fill-color": "#aaa"
        }
      });
      this.map.addLayer({
        id: "features-fill-selected",
        type: "fill",
        source: "features",
        "source-layer": this.featureDetails.layer,
        paint: {
          "fill-opacity": 0.3,
          "fill-color": "#1e86e9"
        },
        filters: ["==", "GEOID", ""]
      });

      this.map.addLayer({
        id: "features-labels",
        type: "symbol",
        source: "features",
        "source-layer": this.featureDetails.labelLayer,
        layout: {
          "text-font": ["DIN Offc Pro Bold", "Arial Unicode MS Regular"],
          "text-field": ["get", this.featureDetails.labelField],
          "text-size": 16,
          "text-justify": "auto",
          "text-anchor": "bottom"
        },
        paint: {
          "text-color": "#1e86e9",
          "text-halo-color": "#fff",
          "text-halo-width": 1
        }
      });
    },
    async setSelected() {
      if (this.disableSelection) {
        this.map.setFilter("features-fill-selected", null);
        return;
      }
      if (!this.featureDetails || !this.map.isStyleLoaded()) {
        return;
      }
      let featureId = null;
      let featureBbox = null;
      if (this.map.isStyleLoaded() && this.map.isSourceLoaded("features")) {
        let matchedFeatured = [];
        if (this.feature === "county" && this.selectedState) {
          const feature = Object.values(states).find(
            s => s.name.toLowerCase() === this.selectedState.toLowerCase()
          );
          if (feature.bbox) {
            featureBbox = feature.bbox;
          }
          const allFeatures = this.map.queryRenderedFeatures({
            layers: ["features-fill"]
          });
          matchedFeatured = allFeatures
            .filter(f => f.properties.GEOID.substr(0, 2) === feature.id)
            .map(f => f.properties.GEOID);
        } else if (this.feature === "township" && this.selectedCounty) {
          const allFeatures = this.map.queryRenderedFeatures({
            layers: ["features-fill"]
          });
          matchedFeatured = allFeatures
            .filter(
              f => f.properties.GEOID.substr(0, 5) === this.selectedCounty
            )
            .map(f => f.properties.GEOID);
        }
        if (matchedFeatured.length > 0) {
          const uniqueFeatured = [...new Set(matchedFeatured)];
          // this.map.setFilter('features-line', ['match', ['get', 'GEOID'], uniqueFeatured, true, false]);
          this.map.setFilter("features-fill-selected", [
            "match",
            ["get", "GEOID"],
            uniqueFeatured,
            true,
            false
          ]);
          this.map.setFilter("features-labels", [
            "match",
            ["get", "GEOID"],
            uniqueFeatured,
            true,
            false
          ]);
        }
      }
      if (this.selectedFeature) {
        featureId = this.selectedFeature.id;
        featureBbox = this.selectedFeature.bbox;
      } else {
        const point = await this.map.project(this.center);
        const features = this.map.queryRenderedFeatures(point, {
          layers: ["features-fill"]
        });
        if (features.length > 0) {
          const feature = features[0];
          featureId = feature.id;
          if (this.feature === "state") {
            const stateFeature = Object.values(states).find(
              s => s.id === featureId
            );
            if (stateFeature) {
              featureBbox = stateFeature.bbox;
            }
          } else {
            featureBbox = bbox(feature);
          }
        }
      }
      if (featureId) {
        this.map.setFilter("features-fill-selected", [
          "==",
          "GEOID",
          featureId
        ]);
      }

      if (!this.fitBounds && featureBbox) {
        this.map.fitBounds(featureBbox, {
          linear: true,
          padding: 50
        });
        this.fitBounds = true;
      }
    },
    refreshMap() {
      this.fitBounds = false;
      this.renderFeatures();
      this.setSelected();
    }
  },
  mounted() {
    if (isHeadless()) {
      return;
    }
    mapboxgl.accessToken = import.meta.env.VITE_APP_MAPBOX_TOKEN;
    this.map = new mapboxgl.Map({
      container: this.$refs.map,
      zoom: this.zoom,
      center: this.center,
      bounds: this.bbox,
      maxZoom: 17,
      scrollZoom: false,

      style: this.style
    });
    this.map.on("load", () => {
      this.popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
      });
      this.map.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        "top-right"
      );

      if (this.showMarker) {
        this.marker = new mapboxgl.Marker({
          draggable: true,
          color: "#1e86e9"
        })
          .setLngLat(this.center)
          .addTo(this.map);
        this.marker.on("dragend", () => {
          const lngLat = this.marker.getLngLat();
          this.$emit("markerMove", lngLat);
        });
        if (this.title) {
          this.marker.getElement().title = this.title;
        }
      }

      this.map.on("sourcedata", this.setSelected);

      this.map.on("click", "features-fill", e => {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: ["features-fill"]
        });
        if (features.length > 0 && features[0].properties) {
          // console.log(features[0].properties);
          this.$emit("featureClick", features[0].properties);
        }
      });
      // Change the cursor to a pointer when the mouse is over the places layer.
      this.map.on("mousemove", "features-fill", e => {
        this.map.getCanvas().style.cursor = "pointer";
        if (this.showNameOnHover) {
          const description = e.features[0].properties.FULLNAME;
          this.popup
            .setLngLat(e.lngLat)
            .setHTML(description)
            .addTo(this.map);
        }
      });

      // Change it back to a pointer when it leaves.
      this.map.on("mouseleave", "features-fill", () => {
        this.map.getCanvas().style.cursor = "";
        this.popup.remove();
      });

      this.renderFeatures();
    });
  },
  watch: {
    center() {
      if (this.showMarker && this.marker) {
        this.marker.setLngLat(this.center);
      }
      this.map.panTo(this.center);
      this.refreshMap();
    },
    title() {
      if (this.marker) {
        this.marker.getElement().title = this.title;
      }
    },
    featureDetails() {
      this.map.setZoom(this.zoom);
      if (this.center) {
        this.map.panTo(this.center);
      }
      this.refreshMap();
    },
    selectedState() {
      this.refreshMap();
    },
    selectedCounty() {
      this.refreshMap();
    },
    selectedFeature() {
      this.refreshMap();
    },
    disableSelection() {
      this.refreshMap();
    },
    style() {
      this.map.setStyle(this.style);
    }
  },
  beforeUnmount() {
    this.map.remove();
  }
};
</script>
